




import { default as Component } from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import Basic from '@/components/widgets/Basic.vue';
import SubmitButton from '@/components/widgets/SubmitButton.vue';
import { Watch } from 'vue-property-decorator';
import IocContainer from '@/container/IocContainer';
import SERVICES from '@/container/Services';
import Api from '@/interfaces/Api';
import Tracking from '@/interfaces/Tracking';
import ProductInfoWidget from './ProductInfoWidget.vue';
import InternationalProductInfoWidget from './international/InternationalProductInfoWidget.vue';
import QcellsProductInfoWidget from './qcells/QcellsProductInfoWidget.vue';
import { AxiosResponse } from 'axios';
import NonCommodityBase from './NonCommodityBase.vue';
import * as Sentry from '@sentry/vue';

const ApiProvider = IocContainer.get<Api>(SERVICES.API);
const trackingProvider = IocContainer.get<Tracking>(SERVICES.TRACKING);

@Component({
  components: {
    'product-info': ProductInfoWidget,
    'international-info': InternationalProductInfoWidget,
    'qcells-product-info': QcellsProductInfoWidget,
    SubmitButton
  }
})
export default class NonCommodityReviewWidget extends NonCommodityBase {
  @Prop() public extendedDataParentKey!: string;

  public isSubmitting: boolean = false;
  public dataReview: any = {};
  public paymentDetails: {
    bank: string;
    bic: string;
  } = {
    bank: '',
    bic: ''
  };
  public products: any[] = [];
  public providersInReview: any[] = [];
  public errorMessages: string[] = [];
  public validated = false;
  public inCreation = false;
  public orderCreated = false;
  public orderFailed = false;
  public orderId: any = null;
  public termsAndConditions = false;
  public privacy = false;
  public adsAccepted = false;

  protected isSubmittingAdsAccepted: boolean = false;
  protected isLoadingDataReview: boolean = false;

  public get isLoading(): boolean {
    return this.isSubmittingAdsAccepted || this.isLoadingDataReview;
  }

  @Watch('adsAccepted')
  public handleAdsAccepted(): void {
    if (this.isSubmitting || this.orderCreated || this.orderFailed) {
      return;
    }
    this.isSubmittingAdsAccepted = true;
    ApiProvider.sessionUpdate({ adsMail: this.adsAccepted }).then(() => {
      this.loadDataReview();
      this.isSubmittingAdsAccepted = false;
    });
  }

  protected onBeforeMount(): void {
    this.handleAdsAccepted();
    this.extendDataWithDefault();
  }

  public beforeMount(): void {
    this.onBeforeMount();
  }

  protected extendDataWithDefault(): void {
    this.extendedData = this.sessionState.extendedData;
  }

  public mounted(): void {
    this.onMounted();
  }

  protected onMounted(): void {
    this.products = this.store.getters.getAvailableProducts;
    this.providersInReview = this.store.getters.getAvailableProviders;
    this.loadDataReview();
    ApiProvider.getProducts().then((response) => {
      this.products = response.data;
      this.products.map((product) => {
        product.initialDurationType = this.getDurationType(product.initialDurationType, product.initialDuration);
        product.renewalDurationType = this.getDurationType(product.renewalDurationType, product.renewalDuration);
        product.cancellationPeriodDaysType = this.getDurationType(
          product.cancellationPeriodDaysType,
          product.cancellationPeriodDays
        );
        product.fixedPriceMonthsType = this.getDurationType('MONTHS', product.fixedPriceMonths);
        return product;
      });
    });
  }

  protected loadDataReview(): void {
    this.isLoadingDataReview = true;
    this.dataReview = this.store.getters.getSession;
    ApiProvider.sessionRead()
      .then((response) => {
        this.store.commit('SET_SESSION', response.data);
        this.linkThgConfigExtendedData();
        this.dataReview = this.store.getters.getSession;
        if (this.dataReview.iban) {
          ApiProvider.getBicAndBank(this.dataReview.iban).then((responseIban) => {
            this.paymentDetails.bank = responseIban.data.bank;
            this.paymentDetails.bic = responseIban.data.bic;
          });
        }
        this.isLoadingDataReview = false;
      })
      .catch((error) => {
        if (typeof error.response !== 'undefined' && error.response.data.error.message === 'session outdated') {
          localStorage.removeItem(process.env.VUE_APP_SESSION_STORAGE_KEY);
          window.location.href = document.referrer || window.location.origin;
        }
        this.isLoadingDataReview = false;
      });
  }

  get startOfDelivery(): string | null {
    switch (this.reviewDataSet.customerSpecification) {
      case 'earliest_possible_date':
        return this.$t('delivery.widget.delivery.asap').toLocaleString();
      case 'desired_date':
        return this.reviewDataSet.desiredDate;
      case 'terminated_in_person':
        return this.reviewDataSet.terminationDate;
      case 'relocation_at':
        return this.reviewDataSet.relocationDate;
    }
    return null;
  }

  get billingSalutationDisplay(): string {
    return this.getSalutationForKey(this.reviewDataSet.billingSalutation);
  }

  get salutationDisplay(): string {
    return this.getSalutationForKey(this.reviewDataSet.salutation);
  }

  get reviewDataSet(): any {
    return this.dataReview;
  }

  get bic(): string {
    return this.paymentDetails.bic;
  }

  get bank(): string {
    return this.paymentDetails.bank;
  }

  get product(): { productCode: string | null; productName: string | null } {
    const product = this.products.filter((p) => {
      return p.productCode === this.dataReview.productCode;
    })[0];

    return product
      ? product
      : {
          productName: null
        };
  }

  protected pushErrorMessage(messageString: string, errorObject: Record<any, any> = []): void {
    this.errorMessages.push(messageString);
  }

  public goBack(): void {
    this.$emit('back');
  }

  protected async updateDateConfirmationAndOwnerConsent(): Promise<void | AxiosResponse<any>> {
    this.updateExtendedDataHook();
    const data = {
      extendedData: JSON.stringify(this.store.state.journey_session.extendedData)
    };
    return ApiProvider.sessionUpdate(data);
  }

  public updateExtendedDataHook(): void {
    console.warn('The default version of updateExtendedDataHook is called in NonCommodityReviewWidget, extend it!');
  }

  public restartJourney(): void {
    this.$emit('restartJourney');
  }

  public submitOrder(): void {
    this.isSubmitting = true;
    trackingProvider.orderSent();
    this.inCreation = true;
    this.updateDateConfirmationAndOwnerConsent().then((response) => {
      ApiProvider.create()
        .then((response) => {
          if (response.data.success) {
            const id = response.data?.data?.id ?? response.data.subSessions?.[0]?.id ?? null;
            let product = this.store.getters.getNonCommodityProduct;
            if (this.isFleetFlow) {
              product = {
                name: 'Fleet'
              };
            }
            trackingProvider.thgFinalStep(product, id);
            this.orderCreated = true;

            this.$emit('orderCreated', response.data);
            this.$emit('reviewSubmit');

            this.$store.dispatch('setOrderId', id);
          } else {
            trackingProvider.orderFailed();
            this.pushErrorMessage(this.getErrorMessage(response), response.data);
            this.$emit('orderFailed', response.data);
          }
        })
        .catch((error) => {
          console.error(error);

          trackingProvider.orderFailed();

          if (
            error.response &&
            error.response.status !== 500 &&
            error.response.data.error &&
            error.response.data.error.message
          ) {
            this.pushErrorMessage(error.response.data.error.message, error.response.data);
          } else {
            Sentry.captureException(new Error(error));
            this.pushErrorMessage(this.$t('review.widget.error').toString());
          }

          this.$emit('orderFailed', error.response && error.response.data ? error.response.data : error);
        })
        .finally(() => {
          this.inCreation = false;
          this.isSubmitting = false;
          this.orderFailed = true;
        });
    });
  }
}
